/**
 * WebView Message Service
 * Handles PostMessage communication with the parent WebView
 */

import { WebViewMessage, WebViewMessageType, WebViewMessageHandlers } from '../types';

export class WebViewMessageService {
  private static instance: WebViewMessageService;
  private handlers: WebViewMessageHandlers = {};
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private hasSentReady: boolean = false;

  private constructor() {
    this.initializeListener();
  }

  static getInstance(): WebViewMessageService {
    if (!WebViewMessageService.instance) {
      WebViewMessageService.instance = new WebViewMessageService();
    }
    return WebViewMessageService.instance;
  }

  /**
   * Initialize message listener
   */
  private initializeListener(): void {
    this.messageListener = (event: MessageEvent) => {
      // Validate message origin if needed
      // if (event.origin !== 'expected-origin') return;

      try {
        const message = event.data as WebViewMessage;
        
        // Check if it's a valid WebView message
        if (!message.type || !Object.values(WebViewMessageType).includes(message.type)) {
          return;
        }

        // Call registered handler
        const handler = this.handlers[message.type];
        if (handler) {
          handler(message.data);
        }
      } catch (error) {
        console.error('Error processing WebView message:', error);
      }
    };

    window.addEventListener('message', this.messageListener);
  }

  /**
   * Register a message handler
   */
  on(type: WebViewMessageType, handler: (data: any) => void): void {
    this.handlers[type] = handler;
  }

  /**
   * Remove a message handler
   */
  off(type: WebViewMessageType): void {
    delete this.handlers[type];
  }
  
  /**
   * Add a message handler and return cleanup function
   */
  addHandler(type: WebViewMessageType, handler: (data: unknown) => void): () => void {
    this.handlers[type] = handler;
    return () => {
      delete this.handlers[type];
    };
  }

  /**
   * Send message to parent WebView
   */
  send(type: WebViewMessageType, data?: any): void {
    const message: WebViewMessage = {
      type,
      data,
      timestamp: Date.now(),
    };

    // Send to parent window
    if (window.parent !== window) {
      window.parent.postMessage(message, '*');
    }

    // Also send to opener if exists (for popup scenarios)
    if (window.opener) {
      window.opener.postMessage(message, '*');
    }
  }

  /**
   * Send ready message
   */
  sendReady(): void {
    // Only send ready message once
    if (this.hasSentReady) {
      console.log('WebView: Ready message already sent, skipping...');
      return;
    }
    
    this.hasSentReady = true;
    this.send(WebViewMessageType.READY, {
      version: '1.0.0',
      timestamp: Date.now(),
    });
    console.log('WebView: Sent ready message');
  }

  /**
   * Send auth success message
   */
  sendAuthSuccess(userId: string): void {
    this.send(WebViewMessageType.AUTH_SUCCESS, {
      userId,
      timestamp: Date.now(),
    });
  }

  /**
   * Send auth error message
   */
  sendAuthError(error: Error): void {
    this.send(WebViewMessageType.AUTH_ERROR, {
      error: error.message,
      timestamp: Date.now(),
    });
  }

  /**
   * Send test complete message
   */
  sendTestComplete(results: any): void {
    this.send(WebViewMessageType.TEST_COMPLETE, results);
  }

  /**
   * Send answer change message
   */
  sendAnswerChange(questionId: string, answer: any): void {
    this.send(WebViewMessageType.ANSWER_CHANGE, {
      questionId,
      answer,
      timestamp: Date.now(),
    });
  }

  /**
   * Send recording complete message
   */
  sendRecordingComplete(recordingData: any): void {
    this.send(WebViewMessageType.RECORDING_COMPLETE, recordingData);
  }

  /**
   * Send error message
   */
  sendError(error: Error): void {
    this.send(WebViewMessageType.ERROR, {
      message: error.message,
      stack: error.stack,
      timestamp: Date.now(),
    });
  }


  /**
   * Cleanup
   */
  destroy(): void {
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }
    this.handlers = {};
  }
}